import React from "react";
import type {
  PublicComment,
  PublicReply,
  CommentModes,
  CommentAttachment
} from "../types";
import { relativeTime } from "../utils";
import Avatar from "./Avatar";
import {
  ThumbIcon,
  ReplyIcon,
  FlagIcon,
  ShieldIcon,
  EyeIcon,
  LockIcon,
  FileIcon
} from "./icons";

interface Props {
  comment: PublicComment;
  modes: CommentModes;
  onVote: (commentId: number) => void;
  onFlag: (commentId: number) => void;
  replyOpen: boolean;
  onOpenReply: (commentId: number) => void;
  onCancelReply: (commentId: number) => void;
  replyDraft: string;
  onReplyDraftChange: (commentId: number, text: string) => void;
  onSendReply: (parentId: number) => void;
  replySubmitting: boolean;
  reported: boolean;
  staffBadgeLabel: string;
  // per-reply admin "post on behalf of the agency" toggle
  canPostAsAdmin: boolean;
  orgLabel: string;
  replyAsAdmin: boolean;
  onToggleReplyAsAdmin: (commentId: number, value: boolean) => void;
}

const anon = (modes: CommentModes) => modes.identity === "anonymous";

// Image attachments render inline; documents render as a download link with a file icon.
function AttachmentList({
  attachments
}: {
  attachments?: CommentAttachment[];
}) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="pc-cmt-atts">
      {attachments.map((a, i) =>
        a.type === 0 ? (
          <a
            key={i}
            className="pc-att-img"
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={a.url} alt={a.fileName} />
          </a>
        ) : (
          <a
            key={i}
            className="pc-att-doc"
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FileIcon className="pc-att-doc-ic" />
            <span className="pc-att-doc-name">{a.fileName}</span>
          </a>
        )
      )}
    </div>
  );
}

function PositionChip({ position }: { position: PublicComment["position"] }) {
  if (position === "favor")
    return <span className="pc-pos-chip pc-pos-favor">In favor</span>;
  if (position === "oppose")
    return <span className="pc-pos-chip pc-pos-oppose">Opposed</span>;
  return null;
}

function ReplyRow({
  reply,
  modes,
  staffBadgeLabel
}: {
  reply: PublicReply;
  modes: CommentModes;
  staffBadgeLabel: string;
}) {
  return (
    <div className={`pc-reply${reply.isStaff ? " pc-reply-staff" : ""}`}>
      <Avatar
        name={reply.authorName}
        isStaff={reply.isStaff}
        anonymous={anon(modes)}
        size="sm"
      />
      <div className="pc-cmt-main">
        <div className="pc-cmt-head">
          <span className="pc-cmt-name">{reply.authorName}</span>
          {reply.isStaff && (
            <span className="pc-staff-badge">
              <ShieldIcon />
              {staffBadgeLabel}
            </span>
          )}
          <span className="pc-cmt-time">
            {relativeTime(reply.createdAtUtc)}
          </span>
        </div>
        <p className="pc-cmt-body">{reply.body}</p>
        <AttachmentList attachments={reply.attachments} />
      </div>
    </div>
  );
}

const CommentCard: React.FC<Props> = ({
  comment,
  modes,
  onVote,
  onFlag,
  replyOpen,
  onOpenReply,
  onCancelReply,
  replyDraft,
  onReplyDraftChange,
  onSendReply,
  replySubmitting,
  reported,
  staffBadgeLabel,
  canPostAsAdmin,
  orgLabel,
  replyAsAdmin,
  onToggleReplyAsAdmin
}) => {
  // Civility-held comment, visible only to its author.
  if (comment.pending) {
    return (
      <div className="pc-cmt pc-cmt-pending">
        <Avatar
          name={comment.authorName}
          isStaff={comment.isStaff}
          anonymous={anon(modes)}
        />
        <div className="pc-cmt-main">
          <div className="pc-cmt-head">
            <span className="pc-cmt-name">{comment.authorName}</span>
            <span className="pc-cmt-time">
              {relativeTime(comment.createdAtUtc)}
            </span>
          </div>
          <p className="pc-cmt-body">{comment.body}</p>
          <div className="pc-pending-note">
            <EyeIcon />
            <span>
              <strong>Visible only to you · under review.</strong> Your comment
              was recorded on the public record but is hidden from public view
              pending a moderator&rsquo;s civility check.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pc-cmt">
      <Avatar
        name={comment.authorName}
        isStaff={comment.isStaff}
        anonymous={anon(modes)}
      />
      <div className="pc-cmt-main">
        <div className="pc-cmt-head">
          <span className="pc-cmt-name">{comment.authorName}</span>
          {comment.isStaff && (
            <span className="pc-staff-badge">
              <ShieldIcon />
              {staffBadgeLabel}
            </span>
          )}
          {modes.positionTags && <PositionChip position={comment.position} />}
          {comment.isPrivate && (
            <span
              className="pc-private-badge"
              title="Private - visible only to you and staff"
            >
              <LockIcon />
              Private
            </span>
          )}
          <span className="pc-cmt-time">
            {relativeTime(comment.createdAtUtc)}
          </span>
        </div>
        <p className="pc-cmt-body">{comment.body}</p>
        <AttachmentList attachments={comment.attachments} />

        <div className="pc-cmt-actions">
          {modes.upvotes && (
            <button
              type="button"
              className={`pc-cact pc-cact-vote${
                comment.votedByMe ? " pc-on" : ""
              }`}
              onClick={() => onVote(comment.id)}
              aria-pressed={comment.votedByMe}
              aria-label={comment.votedByMe ? "Remove upvote" : "Upvote"}
            >
              <ThumbIcon />
              <span className="pc-ct">{comment.votes}</span>
            </button>
          )}
          {modes.replies && (
            <button
              type="button"
              className="pc-cact"
              onClick={() => onOpenReply(comment.id)}
            >
              <ReplyIcon />
              Reply
            </button>
          )}
          {modes.flag &&
            (reported ? (
              <button
                type="button"
                className="pc-cact pc-cact-flag pc-sep"
                disabled
                aria-label="Reported to moderators"
              >
                <FlagIcon />
                Reported
              </button>
            ) : (
              <button
                type="button"
                className="pc-cact pc-cact-flag pc-sep"
                onClick={() => onFlag(comment.id)}
              >
                <FlagIcon />
                Flag
              </button>
            ))}
        </div>

        {modes.replies && comment.replies.length > 0 && (
          <div className="pc-replies">
            {comment.replies.map((r) => (
              <ReplyRow
                key={r.id}
                reply={r}
                modes={modes}
                staffBadgeLabel={staffBadgeLabel}
              />
            ))}
          </div>
        )}

        {modes.replies && replyOpen && (
          <div className="pc-replybox">
            <textarea
              placeholder="Write a reply..."
              value={replyDraft}
              onChange={(e) => onReplyDraftChange(comment.id, e.target.value)}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            {canPostAsAdmin && (
              <label className="pc-post-as pc-post-as-reply">
                <input
                  type="checkbox"
                  checked={replyAsAdmin}
                  onChange={(e) =>
                    onToggleReplyAsAdmin(comment.id, e.target.checked)
                  }
                />
                <ShieldIcon />
                Reply on behalf of {orgLabel}
              </label>
            )}
            <div className="pc-rb-foot">
              <button
                type="button"
                className="pc-btn pc-btn-primary pc-btn-sm"
                onClick={() => onSendReply(comment.id)}
                disabled={replySubmitting}
              >
                {replySubmitting ? (
                  <>
                    <span className="pc-spin" aria-hidden="true" />
                    Posting reply...
                  </>
                ) : (
                  "Reply"
                )}
              </button>
              <button
                type="button"
                className="pc-btn pc-btn-ghost"
                onClick={() => onCancelReply(comment.id)}
                disabled={replySubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentCard;
